// Anillos concentricos (video "Mide tu negocio por periodos"): cada anillo se recarga con un resorte al cambiar
// el valor; al pasar el cursor por la leyenda o tocarlo, se resalta y los demas se apagan.
import { useEffect, useRef } from 'react';

import { SpringValue } from '../../lib/spring';
import { Cifra } from './Cifra';
import { F } from './formato';

export type Anillo = { k: string; nombre: string; pct: number; color: string; detalle?: string };

function Arco({ r, ancho, pct, color, apagado, brilla, onClick }: { r: number; ancho: number; pct: number; color: string; apagado: boolean; brilla: boolean; onClick?: () => void }) {
  const ref = useRef<SVGCircleElement>(null);
  const L = 2 * Math.PI * r;
  const s = useRef<SpringValue | null>(null);
  useEffect(() => {
    if (!s.current) s.current = new SpringValue(L, (v) => ref.current?.setAttribute('stroke-dashoffset', String(v)), 0.5);
    s.current.go(L * (1 - Math.min(1, Math.max(0, pct) / 100)), { response: 0.7, damping: 0.82 });
  }, [pct, L]);
  useEffect(() => () => s.current?.stop(), []);
  return (
    <circle
      ref={ref}
      cx={110}
      cy={110}
      r={r}
      fill="none"
      stroke={color}
      strokeWidth={brilla ? ancho + 3 : ancho}
      strokeLinecap="round"
      strokeDasharray={L}
      strokeDashoffset={L}
      transform="rotate(-90 110 110)"
      className={`an-arco${apagado ? ' apagado' : ''}${brilla ? ' brilla' : ''}`}
      style={{ color, cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    />
  );
}

export function Anillos({ items, centro, sub, sel, foco, onClick, fmtCentro = F.n }: { items: Anillo[]; centro: number; sub: string; sel?: string | null; foco?: string | null; onClick?: (a: Anillo) => void; fmtCentro?: (v: number) => string }) {
  const R = 92, ancho = 11, sep = 5;
  return (
    <div className="anillos-w">
      <svg viewBox="0 0 220 220" className="anillos" role="img" aria-label={items.map((i) => `${i.nombre} ${F.p(i.pct)}`).join(', ')}>
        {items.map((it, k) => {
          const r = R - k * (ancho + sep);
          return (
            <g key={it.k}>
              <circle cx={110} cy={110} r={r} className="an-pista" strokeWidth={ancho} fill="none" />
              <Arco r={r} ancho={ancho} pct={it.pct} color={it.color} apagado={(!!sel && sel !== it.k) || (!!foco && foco !== it.k)} brilla={foco === it.k} onClick={onClick ? () => onClick(it) : undefined} />
            </g>
          );
        })}
      </svg>
      <div className="an-centro">
        <Cifra className="an-v" valor={centro} fmt={fmtCentro} />
        <span className="an-s">{sub}</span>
      </div>
    </div>
  );
}

export function LeyendaAnillos({ items, sel, onClick, onFoco }: { items: Anillo[]; sel?: string | null; onClick?: (a: Anillo) => void; onFoco?: (k: string | null) => void }) {
  return (
    <div className="an-ley">
      {items.map((it) => {
        const Tag = onClick ? 'button' : 'div';
        return (
          <Tag
            key={it.k}
            type={onClick ? 'button' : undefined}
            className={`an-li${sel && sel !== it.k ? ' apagado' : ''}`}
            onClick={onClick ? () => onClick(it) : undefined}
            onPointerEnter={() => onFoco?.(it.k)}
            onPointerLeave={() => onFoco?.(null)}
            onFocus={() => onFoco?.(it.k)}
            onBlur={() => onFoco?.(null)}
            tabIndex={onClick ? undefined : 0}
          >
            <i style={{ background: it.color }} />
            <span>
              {it.nombre}
              {it.detalle ? <small>{it.detalle}</small> : null}
            </span>
            <b className="mono">{F.p(it.pct)}</b>
          </Tag>
        );
      })}
    </div>
  );
}
